import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { FASTAPI_HOST } from '../settings/config';
import { cognitiveLevelsData, visualStylesData, voicesData } from '../settings/staticData';
import { DEFAULT_BUDDY } from '../settings/buddyProfiles';
import { getBuddyProfiles } from '../settings/buddySessionCache';
import { normalizeBuddyVoiceId } from '../settings/buddyVoiceCatalog';
import MicTranscribeButton from '../components/MicTranscribeButton';
import MindMapPanel from '../components/MindMapPanel';
import reconcileMindMap from '../components/mindMapReconcile';
import TextStoryPage from './TextStoryPage';
import CreationSettingsPage from './CreationSettingsPage';
import BuddyManagerDialog from '../components/BuddyManagerDialog';
import ConversationStorageControls from '../components/ConversationStorageControls';
import {
  BuddyWorkspace,
  ConversationScaffoldAids,
  ConversationTimeline,
  EndConversationControl,
  WorkspaceComposerArea,
  WorkspaceHeader,
} from '../components/buddy-workspace';
import { IMAGE_UPLOAD_ACCEPT, prepareImageForUpload } from '../utils/prepareImageForUpload';
import { hasPaidPlanAccess } from '../utils/planAccess';

const cardSx = {
  borderRadius: 4,
  border: '1px solid #D6DED2',
  boxShadow: '0 18px 50px rgba(52, 83, 45, 0.12)',
  background: '#fff',
};

const PHOTO_STORY_STYLES = [
  {
    value: 'clear_direct',
    label: 'Clear & Direct',
    labelKey: 'photo_story_style_clear',
    description: 'Literal wording, explicit sequence, and no figurative language.',
    descriptionKey: 'photo_story_style_clear_desc',
  },
  {
    value: 'warm_expressive',
    label: 'Warm & Expressive',
    labelKey: 'photo_story_style_warm',
    description: 'Concrete storytelling with gentle emotion, vivid verbs, and natural flow.',
    descriptionKey: 'photo_story_style_warm_desc',
  },
  {
    value: 'imaginative',
    label: 'Imaginative',
    labelKey: 'photo_story_style_imaginative',
    description: 'Playful comparisons and richer words without inventing events or feelings.',
    descriptionKey: 'photo_story_style_imaginative_desc',
  },
];

const PHOTO_STORY_OPENING_MESSAGE = 'Upload a real-life photo. I will give gentle context, then help the child tell what is happening or happened in their own words.';
const SILENT_WAV_DATA_URL = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

export default function PhotoStoryPage({ currentLang = 'English', onUiLanguageChange, onPurchase }) {
  const { user, isSignedIn } = useUser();
  const { getToken, signOut } = useAuth();

  // Reuse the same Role Settings data (name/language/age) as the rest of the app.
  const [childName, setChildName] = useState('');
  const [childAge, setChildAge] = useState(null);
  const conversationLanguage = currentLang || 'English';
  const [storyLanguage, setStoryLanguageState] = useState(currentLang || 'English');
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [roleSettingDialogOpen, setRoleSettingDialogOpen] = useState(false);
  const [buddySettingsOpen, setBuddySettingsOpen] = useState(false);
  const [photoStoryStarted, setPhotoStoryStarted] = useState(false);
  const [buddy, setBuddy] = useState(DEFAULT_BUDDY);
  const [uploadDisclaimerOpen, setUploadDisclaimerOpen] = useState(false);
  const [storyStyle, setStoryStyle] = useState('clear_direct');
  const [caregiverNotes, setCaregiverNotes] = useState('');
  const [leafyVoice, setLeafyVoice] = useState('ai_01');
  const [leafySpeechSpeed, setLeafySpeechSpeed] = useState(1.0);

  // Full Role Settings (same fields as RoleSettingPage / HomePage)
  const [gender, setGender] = useState('Male');
  const [visualStyle, setVisualStyle] = useState(() => (visualStylesData?.[0]?.key ? visualStylesData[0].key : ''));
  const [interest, setInterest] = useState('');
  const [cognitiveLevel, setCognitiveLevel] = useState(() => (cognitiveLevelsData?.[0]?.Name ? cognitiveLevelsData[0].Name : 'Intermediate'));
  const [conversationLevel, setConversationLevel] = useState('Intermediate');
  const [rolePages, setRolePages] = useState(6);
  const [imageStyle, setImageStyle] = useState('Gentle Storybook Manga');
  const normalizedCognitiveLevel = String(conversationLevel || 'Intermediate').toLowerCase();
  const conversationSupportLevel = ['basic', 'intermediate', 'advanced'].includes(normalizedCognitiveLevel)
    ? normalizedCognitiveLevel
    : 'intermediate';

  const setStoryLanguage = (nextLanguage) => {
    const normalizedLanguage = nextLanguage || 'English';
    setStoryLanguageState(normalizedLanguage);
  };

  // UI language and Role story language are independent settings.
  const [translations, setTranslations] = useState({});
  const t = useMemo(() => translations?.[currentLang] || {}, [translations, currentLang]);

  const getImageStylesInLanguage = (lang) => ([
    'Gentle Storybook Manga',
    'Watercolor',
    'Simple Line Comics',
    'Educational Comic',
    'Cartoon',
  ]);

  // Voice mode (future-proof for OpenAI Realtime / GPT-Live)
  // - turn_based: STT (/api/stt) + Photo Story TTS (/api/photo-story/tts)
  // - realtime: placeholder for now (will later use WebRTC/WebSocket)
  const [voiceMode, setVoiceMode] = useState('turn_based');

  // Story narration voice remains separate from Leafy's Photo Story talk voice.
  const [ttsVoice, setTtsVoice] = useState('female');

  // Upload input (opened after the user agrees to the rights reminder)
  const uploadInputRef = useRef(null);

  // Prevent overlapping audio when user taps repeatedly
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const ttsAbortRef = useRef(null);
  const lastSpokenTextRef = useRef('');
  const lastBackendTtsRef = useRef({ key: '', blobs: [] });
  const chatContainerRef = useRef(null);
  const leafySpeechSpeedRef = useRef(leafySpeechSpeed);

  useEffect(() => {
    leafySpeechSpeedRef.current = leafySpeechSpeed;
    if (audioRef.current) {
      audioRef.current.playbackRate = leafySpeechSpeed;
      audioRef.current.preservesPitch = true;
      audioRef.current.mozPreservesPitch = true;
      audioRef.current.webkitPreservesPitch = true;
    }
  }, [leafySpeechSpeed]);

  const [pageCount, setPageCount] = useState(6);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [photoPreviewExpanded, setPhotoPreviewExpanded] = useState(false);

  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const [sceneSummary, setSceneSummary] = useState('');

  // Photo-story orchestrator session (backend decides next question)
  const [sessionId, setSessionId] = useState('');
  const [storageMode, setStorageMode] = useState('temporary');
  const [sessionVersion, setSessionVersion] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentChoices, setCurrentChoices] = useState([]);
  const [currentHint, setCurrentHint] = useState('');
  const [conversationActions, setConversationActions] = useState([]);
  const [showScaffoldAids, setShowScaffoldAids] = useState(true);
  const [voiceBroadcastEnabled, setVoiceBroadcastEnabled] = useState(false);
  const [memoryMapDialogOpen, setMemoryMapDialogOpen] = useState(false);
  const [mobileMindMapExpanded, setMobileMindMapExpanded] = useState(false);
  const [mindMapDocument, setMindMapDocument] = useState(null);
  const [mindMapLoading, setMindMapLoading] = useState(false);
  const [mindMapError, setMindMapError] = useState('');
  const [conversationDone, setConversationDone] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [pendingUserMessage, setPendingUserMessage] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [dialogMessages, setDialogMessages] = useState([]); // persistent chat history for UI display
  const [conversationAnswers, setConversationAnswers] = useState([]); // [{question, answer, goal}] (from backend)

  const [storyTitle, setStoryTitle] = useState('');
  const [storyPages, setStoryPages] = useState([]);
  const [caregiverTip, setCaregiverTip] = useState('');
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [mediaErrorSeverity, setMediaErrorSeverity] = useState('error');
  const [userPlan, setUserPlan] = useState(null);
  const [userPlanChecked, setUserPlanChecked] = useState(false);

  const defaultImageStyle = 'Gentle Storybook Manga';

  const canGenerate = !!sessionId
    && conversationActions.some((action) => action.id === 'generate_artifact' && action.enabled)
    && !generating;
  const canAddConversationDetail = conversationActions.some(
    (action) => action.id === 'submit_text' && action.enabled
  );
  const hasMemoryMapData = Boolean(sessionId && conversationAnswers.length > 0);
  const compactMindMapLabel = {
    English: 'My Ideas',
    French: 'Mes idées',
    'Simplified Chinese': '我的想法',
    'Traditional Chinese': '我的想法',
  }[conversationLanguage] || 'My Ideas';
  const applyConversationView = (view) => {
    if (view?.context?.persistence_warning) setError(view.context.persistence_warning);
    setSessionId(view?.session_id || '');
    setStorageMode(view?.context?.storage_mode || 'temporary');
    setSessionVersion(Number(view?.version) || 0);
    setConversationDone(view?.status === 'ready' || view?.status === 'completed');
    setSceneSummary(view?.context?.scene_summary || '');
    setConversationAnswers(Array.isArray(view?.context?.answers) ? view.context.answers : []);
    const messages = Array.isArray(view?.messages)
      ? view.messages.filter((message) => message?.role !== 'system').map((message) => ({
          role: message.role,
          text: message.content,
          kind: message.kind || 'message',
          reaction: message.reaction || 'neutral',
        }))
      : [];
    setDialogMessages(messages);
    const input = view?.input || null;
    setCurrentQuestion(input?.prompt || '');
    setCurrentChoices(Array.isArray(input?.choices) ? input.choices.map((choice) => choice?.label || choice) : []);
    setCurrentHint(input?.hint || '');
    setConversationActions(Array.isArray(view?.actions) ? view.actions : []);
    const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant');
    return { messages, input, lastAssistant };
  };

  const restoreConversationView = async (view) => {
    applyConversationView(view);
    setPhotoStoryStarted(true);
    const sourceUrl = view?.context?.source_image_url || '';
    if (!sourceUrl) return;
    setPhotoPreviewExpanded(false);
    setPhotoPreviewUrl(sourceUrl);
    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) return;
      const blob = await response.blob();
      setPhotoFile(new File([blob], 'saved-photo', { type: blob.type || 'image/jpeg' }));
    } catch (_error) {
      // The signed preview remains useful even if this browser cannot recreate
      // a File object. The conversation itself is still fully restored.
    }
  };

  const chatMessages = useMemo(() => {
    const msgs = Array.isArray(dialogMessages) ? [...dialogMessages] : [];
    if (pendingUserMessage) {
      msgs.push({ role: 'user', text: pendingUserMessage, kind: 'pending' });
    }
    return msgs;
  }, [
    dialogMessages,
    pendingUserMessage,
  ]);

  const lastPlayableAssistantIdx = useMemo(() => {
    for (let i = chatMessages.length - 1; i >= 0; i -= 1) {
      const msg = chatMessages[i];
      if (
        msg?.role === 'assistant'
        && String(msg?.text || '').trim()
      ) {
        return i;
      }
    }
    return -1;
  }, [chatMessages]);

  const latestPlayableAssistantText = lastPlayableAssistantIdx >= 0
    ? String(chatMessages[lastPlayableAssistantIdx]?.text || '').trim()
    : '';

  const storyPagesForEditor = useMemo(() => {
    const splitIntoSentences = (text) => {
      const raw = String(text || '').trim();
      if (!raw) return [''];
      const chunks = raw.match(/[^.!?]+[.!?]?/g) || [raw];
      return chunks.map((s) => s.trim()).filter(Boolean);
    };

    return (storyPages || []).map((p, idx) => ({
      page_number: Number(p?.page || idx + 1),
      sentences: splitIntoSentences(p?.text),
    }));
  }, [storyPages]);

  const ensureAppToken = async (forceRefresh = false) => {
    if (forceRefresh) {
      Cookies.remove('user_token');
    }

    const existing = Cookies.get('user_token');
    if (existing) return existing;
    if (!isSignedIn || !user) return '';

    const clerkToken = await getToken();
    if (!clerkToken) return '';

    const userData = {
      username: user.username || '',
      full_name: user.fullName || '',
      password: '',
      email: user.primaryEmailAddress?.emailAddress,
      external_auth_provider: 'clerk',
      external_user_id: user.id,
    };

    const res = await axios.post(`${FASTAPI_HOST}/api/login_external`, userData, {
      headers: { Authorization: `Bearer ${clerkToken}` },
    });

    const appToken = res.data?.access_token || '';
    if (!appToken) return '';

    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      Cookies.set('user_token', appToken);
    } else {
      Cookies.set('user_token', appToken, { secure: true, sameSite: 'strict' });
    }

    return appToken;
  };

  const runWithAuthRetry = async (requestBuilder) => {
    let token = await ensureAppToken(false);
    if (!token) {
      throw new Error('Session expired. Please sign in again.');
    }

    try {
      return await requestBuilder(token);
    } catch (e) {
      if (e?.response?.status !== 401) throw e;

      token = await ensureAppToken(true);
      if (!token) {
        if (signOut) signOut();
        throw new Error('Session expired. Please sign in again.');
      }
      return await requestBuilder(token);
    }
  };

  const handleStoryStyleChange = async (event) => {
    const nextStyle = event.target.value;
    setStoryStyle(nextStyle);
    try {
      await runWithAuthRetry((token) =>
        axios.put(`${FASTAPI_HOST}/api/user-preferences`, {
          photo_story_style: nextStyle,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
      );
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || saveError?.message || 'Could not save the story style.');
    }
  };

  const stopSpeaking = () => {
    // Cancel in-flight TTS request
    if (ttsAbortRef.current) {
      try {
        ttsAbortRef.current.abort();
      } catch (e) {
        // ignore
      }
      ttsAbortRef.current = null;
    }

    // Stop currently playing audio
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch (e) {
        // ignore
      }
      audioRef.current = null;
    }

    // Revoke object URL
    if (audioUrlRef.current) {
      try {
        URL.revokeObjectURL(audioUrlRef.current);
      } catch (e) {
        // ignore
      }
      audioUrlRef.current = '';
    }

    // Stop browser TTS fallback if used
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  const primeAudioPlayback = () => {
    if (typeof Audio === 'undefined') return;
    const audio = new Audio(SILENT_WAV_DATA_URL);
    audio.volume = 0;
    const playPromise = audio.play();
    if (playPromise?.then) {
      playPromise.then(() => {
        audio.pause();
        audio.src = '';
      }).catch(() => {
        // A later explicit voice tap can still unlock playback if this browser
        // does not accept silent priming.
      });
    }
  };

  // Stop any playing audio if user navigates away/unmounts.
  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, []);

  useEffect(() => {
    fetch('/translations.json')
      .then((res) => res.json())
      .then((data) => setTranslations(data))
      .catch(() => setTranslations({}));
  }, []);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [chatMessages]);

  useEffect(() => {
    let cancelled = false;

    const fetchPlan = async () => {
      try {
        const res = await runWithAuthRetry((token) =>
          axios.get(`${FASTAPI_HOST}/api/get_plan_for_user`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
        if (!cancelled) setUserPlan(res.data?.plan_info || null);
      } catch (e) {
        // Non-blocking for photo story flow.
      } finally {
        if (!cancelled) setUserPlanChecked(true);
      }
    };

    fetchPlan();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user]);

  useEffect(() => {
    let cancelled = false;

    const loadPhotoStorySettings = async () => {
      try {
        const [res, profiles] = await Promise.all([
          runWithAuthRetry((token) => axios.get(`${FASTAPI_HOST}/api/user-preferences`, { headers: { Authorization: `Bearer ${token}` } })),
          getBuddyProfiles({
            fetcher: () => runWithAuthRetry((token) => axios.get(`${FASTAPI_HOST}/api/user-buddies`, { headers: { Authorization: `Bearer ${token}` } })),
          }),
        ]);
        if (cancelled) return;

        const preferences = res.data || {};
        if (
          preferences.ui_language
          && preferences.ui_language !== currentLang
          && typeof onUiLanguageChange === 'function'
        ) {
          onUiLanguageChange(preferences.ui_language);
        }
        if (preferences.photo_story_style) setStoryStyle(preferences.photo_story_style);
        if (preferences.page_count) setPageCount(Number(preferences.page_count));
        setCaregiverNotes(preferences.photo_caregiver_notes || '');
        const preferredId = window.localStorage.getItem('freeTalkBuddyId');
        const selectedBuddy = profiles.find((item) => item.id === preferredId) || profiles.find((item) => item.isDefault) || profiles[0] || DEFAULT_BUDDY;
        const selectedWithAvatar = selectedBuddy;
        setBuddy(selectedWithAvatar);
        if (selectedWithAvatar.voice) setLeafyVoice(normalizeBuddyVoiceId(selectedWithAvatar.voice));
        setLeafySpeechSpeed(Number(preferences.photo_leafy_speech_speed) || 1);
      } catch (e) {
        // Non-blocking: defaults remain available if preferences cannot load.
        console.warn('Failed to load Photo Story settings:', e);
      }
    };

    loadPhotoStorySettings();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user]);

  useEffect(() => {
    const handleBuddyUpdate = (event) => {
      const selected = event.detail;
      if (!selected?.id) return;
      setBuddy(selected);
      if (selected.voice) setLeafyVoice(normalizeBuddyVoiceId(selected.voice));
      if (selected.speed) setLeafySpeechSpeed(Number(selected.speed) || 1);
    };
    window.addEventListener('buddy-settings-updated', handleBuddyUpdate);
    return () => window.removeEventListener('buddy-settings-updated', handleBuddyUpdate);
  }, []);

  const turnBasedSpeakText = async (text, options = {}) => {
    if (!text) return;

    // If user taps again on the same text, treat it as stop.
    if (lastSpokenTextRef.current === text && audioRef.current) {
      stopSpeaking();
      return;
    }

    stopSpeaking();
    lastSpokenTextRef.current = text;

    const speed = Number(leafySpeechSpeed) || 1.0;
    const talkVoice = leafyVoice;
    const key = `photo-story-talk|${talkVoice}|${conversationLanguage || 'English'}|${text}`;

    const playAudioBlobs = async (blobs) => {
      const queue = Array.isArray(blobs) ? blobs.filter(Boolean) : [];
      if (queue.length === 0) return;

      const playAt = async (index) => {
        if (index >= queue.length) {
          stopSpeaking();
          return;
        }

        const url = URL.createObjectURL(queue[index]);
        audioUrlRef.current = url;

        const audio = new Audio(url);
        audio.playbackRate = Number(leafySpeechSpeedRef.current) || 1.0;
        audio.preservesPitch = true;
        audio.mozPreservesPitch = true;
        audio.webkitPreservesPitch = true;
        audioRef.current = audio;

        audio.onended = () => {
          if (audioRef.current !== audio) return;
          audioRef.current = null;
          if (audioUrlRef.current === url) {
            URL.revokeObjectURL(url);
            audioUrlRef.current = '';
          }
          playAt(index + 1).catch(() => {
            stopSpeaking();
            setError('Unable to play backend voice right now. Please try again.');
          });
        };
        audio.onerror = () => {
          stopSpeaking();
          setError('Unable to play backend voice right now. Please try again.');
        };

        await audio.play();
      };

      await playAt(0);
    };

    // Photo Story conversations must use the backend voice. Browser speech is
    // only allowed when a caller opts in explicitly.
    const allowBrowserFallback = options?.allowBrowserFallback === true;
    const requireCacheKeyMatch = options?.requireCacheKeyMatch !== false;
    const cachedBackendBlobs = Array.isArray(lastBackendTtsRef.current?.blobs)
      ? lastBackendTtsRef.current.blobs
      : lastBackendTtsRef.current?.blob
        ? [lastBackendTtsRef.current.blob]
        : [];

    if (
      options?.preferCachedBackend
      && cachedBackendBlobs.length > 0
      && (!requireCacheKeyMatch || lastBackendTtsRef.current.key === key)
    ) {
      await playAudioBlobs(cachedBackendBlobs);
      return;
    }

    // Prefer backend TTS (consistent friendly voice). Fallback to browser TTS.
    try {
      const token = await ensureAppToken(false);
      if (!token) throw new Error('Missing token');

      const controller = new AbortController();
      ttsAbortRef.current = controller;

      // Generative TTS can occasionally treat a question after a blank line as
      // optional. Generate each paragraph separately, then play every returned
      // backend audio segment in order.
      const segments = String(text)
        .split(/\n\s*\n+/)
        .map((segment) => segment.trim())
        .filter(Boolean);
      const responses = await Promise.all(segments.map((segment) => (
        axios.post(`${FASTAPI_HOST}/api/photo-story/tts`, {
          text: segment,
          voice: talkVoice,
          language: conversationLanguage || 'English',
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'arraybuffer',
          signal: controller.signal,
        })
      )));

      const blobs = responses.map((res) => new Blob([res.data], { type: 'audio/mpeg' }));
      ttsAbortRef.current = null;
      lastBackendTtsRef.current = { key, blobs };
      await playAudioBlobs(blobs);
      return;
    } catch (e) {
      // If request was cancelled, do not fall back.
      if (e?.code === 'ERR_CANCELED') return;
      if (!allowBrowserFallback) {
        stopSpeaking();
        setError('Unable to play backend voice right now. Please try again.');
        return;
      }
      console.warn('Backend TTS failed, falling back to browser voice:', e);
    }

    // Browser fallback
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = speed;
    utterance.pitch = 1.05;
    utterance.onend = () => stopSpeaking();
    utterance.onerror = () => stopSpeaking();
    window.speechSynthesis.speak(utterance);
  };

  const realtimeSpeakText = async (text, options = {}) => {
    // Placeholder for GPT-Live / realtime voice.
    // For now, fall back to turn-based so the app keeps working.
    return turnBasedSpeakText(text, options);
  };

  const speakText = async (text, options = {}) => {
    if (voiceMode === 'realtime') {
      return realtimeSpeakText(text, options);
    }
    return turnBasedSpeakText(text, options);
  };

  const toggleVoiceBroadcast = async () => {
    if (voiceBroadcastEnabled) {
      setVoiceBroadcastEnabled(false);
      stopSpeaking();
      return;
    }

    setVoiceBroadcastEnabled(true);
    if (latestPlayableAssistantText) {
      await speakText(latestPlayableAssistantText, {
        preferCachedBackend: true,
        requireCacheKeyMatch: true,
        allowBrowserFallback: false,
      });
    }
  };

  // Load Role Settings defaults (name, story language, age) for this page.
  useEffect(() => {
    let cancelled = false;

    const loadRoleSettings = async () => {
      try {
        const res = await runWithAuthRetry((token) =>
          axios.get(`${FASTAPI_HOST}/api/get_user_roles`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );

        if (cancelled) return;
        const userRole = (res.data?.user_roles && res.data.user_roles[0]) || {};
        setActiveRoleId(res.data?.active_role_id ?? userRole?.id ?? null);
        if (userRole?.name) setChildName(userRole.name);
        if (userRole?.story_lang) setStoryLanguage(userRole.story_lang);
        if (userRole?.age) setChildAge(userRole.age);
        if (userRole?.gender) setGender(userRole.gender);
        if (userRole?.visual_style) setVisualStyle(userRole.visual_style);
        if (userRole?.interest) setInterest(userRole.interest);
        setCognitiveLevel(userRole?.written_language_level || userRole?.cognitive_level || 'Intermediate');
        setConversationLevel(userRole?.conversation_level || userRole?.cognitive_level || 'Intermediate');
        if (userRole?.image_style) setImageStyle(userRole.image_style);

        // Reuse Role Settings voice if present (male/female/boy/girl keys)
        if (userRole?.voice) setTtsVoice(userRole.voice);
      } catch (e) {
        // Non-blocking: user can still type manually if needed.
        console.warn('Failed to load role settings for photo story page:', e);
      }
    };

    loadRoleSettings();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, user]);

 const openUploadDisclaimer = () => setUploadDisclaimerOpen(true);
  const closeUploadDisclaimer = () => setUploadDisclaimerOpen(false);
  const handleUploadAgree = () => {
    closeUploadDisclaimer();
    // Defer so the dialog unmounts before the browser opens the file picker.
    setTimeout(() => {
      if (!uploadInputRef.current) return;
      uploadInputRef.current.value = '';
      uploadInputRef.current.click();
    }, 0);
  };

  const handleSelectPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setAnalyzing(true);
    setVoiceBroadcastEnabled(false);
    stopSpeaking();
    let preparedFile;
    try {
      preparedFile = await prepareImageForUpload(file);
    } catch (preparationError) {
      setError(preparationError?.message || (t?.photo_story_invalid_image_format || 'This image could not be prepared for upload.'));
      setAnalyzing(false);
      return;
    }
    setPhotoFile(preparedFile);
    setPhotoPreviewExpanded(false);
    setPhotoPreviewUrl((currentUrl) => {
      if (currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
      return URL.createObjectURL(preparedFile);
    });

    // Reset analysis + conversation
    setStorageMode('temporary');
    setSceneSummary('');
    setSessionId('');
    setSessionVersion(0);
    setCurrentQuestion('');
    setCurrentChoices([]);
    setCurrentHint('');
    setConversationActions([]);
    setConversationDone(false);
    setCurrentAnswer('');
    setPendingUserMessage('');
    setSubmittingAnswer(false);
    setDialogMessages([]);
    setConversationAnswers([]);
    setMemoryMapDialogOpen(false);
    setMindMapDocument(null);
    setMindMapError('');
    setStoryTitle('');
    setStoryPages([]);
    setCaregiverTip('');
    setStoryDialogOpen(false);
    setMediaUrl('');
    setMedia([]);
    setMediaError('');
    setMediaErrorSeverity('error');

    // Auto-start analysis after upload.
    // (Use the selected file directly to avoid waiting for state to settle.)
    setTimeout(() => startSession(preparedFile), 0);
  };

  const startSession = async (fileOverride = null) => {
    const imageFile = fileOverride || photoFile;

    if (!imageFile) {
      setError('Please upload a live-scene photo first.');
      return;
    }

    setError('');
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('definition', 'photo_memory_interview');
      formData.append('child_name', childName || 'Child');
      if (childAge) formData.append('child_age', String(childAge));
      // `language` remains for older backends; explicit fields keep AI talk and
      // final story writing independent.
      formData.append('language', conversationLanguage || 'English');
      formData.append('conversation_language', conversationLanguage || 'English');
      formData.append('writing_language', storyLanguage || 'English');
      formData.append('conversation_level', conversationSupportLevel);
      formData.append('cognitive_level', String(cognitiveLevel || 'Intermediate').toLowerCase());
      formData.append('buddy_name', buddy.name);
      formData.append('buddy_personality', buddy.personality || '');
      formData.append('caregiver_notes', caregiverNotes.trim());

      const res = await runWithAuthRetry((token) =>
        axios.post(`${FASTAPI_HOST}/api/conversations`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        })
      );

      applyConversationView(res.data);
      setCurrentAnswer('');
      // Opening narration is intentionally silent. The user must explicitly
      // enable AI voice playback from the conversation toolbar.
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'invalid_image_format') {
        setError(t?.photo_story_invalid_image_format || detail.message);
      } else {
        setError(
          (typeof detail === 'string' ? detail : detail?.message)
          || e?.message
          || 'Failed to start photo story session.'
        );
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const commitCurrentAnswerAndNext = async (answerFromInput) => {
    if (voiceBroadcastEnabled) primeAudioPlayback();
    const answer = (answerFromInput ?? currentAnswer).trim() || 'Not sure.';

    if (!sessionId) {
      setError('Please upload a real-life photo so Leafy can gently help tell its story.');
      openUploadDisclaimer();
      return;
    }
    setError('');
    setSubmittingAnswer(true);
    setPendingUserMessage(answer);
    setAnalyzing(true);
    try {
      const payload = {
        request_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        expected_version: sessionVersion,
        type: 'submit_text',
        value: answer,
        conversation_language: currentLang || 'English',
      };

      const res = await runWithAuthRetry((token) =>
        axios.post(`${FASTAPI_HOST}/api/conversations/${sessionId}/actions`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
      );

      const rendered = applyConversationView(res.data);
      if (memoryMapDialogOpen) loadMindMap(res.data?.session_id);
      setCurrentAnswer('');
      setPendingUserMessage('');
      if (rendered.lastAssistant?.text && voiceBroadcastEnabled) {
        await speakText(rendered.lastAssistant.text, { allowBrowserFallback: false });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to send answer.');
    } finally {
      setSubmittingAnswer(false);
      setAnalyzing(false);
    }
  };

  const sendConversationAction = async (type, value = null) => {
    if (voiceBroadcastEnabled) primeAudioPlayback();
    if (!sessionId || !sessionVersion) return;
    setError('');
    setSubmittingAnswer(true);
    try {
      const payload = {
        request_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        expected_version: sessionVersion,
        type,
        value,
        conversation_language: currentLang || 'English',
      };
      const res = await runWithAuthRetry((token) =>
        axios.post(`${FASTAPI_HOST}/api/conversations/${sessionId}/actions`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
      );
      const rendered = applyConversationView(res.data);
      if (memoryMapDialogOpen) loadMindMap(res.data?.session_id);
      if (rendered.lastAssistant?.text && voiceBroadcastEnabled) {
        await speakText(rendered.lastAssistant.text, { allowBrowserFallback: false });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update conversation.');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const loadMindMap = async (targetSessionId = sessionId) => {
    if (!targetSessionId) return;
    setMindMapLoading(true);
    setMindMapError('');
    try {
      const response = await runWithAuthRetry((token) => axios.post(
        `${FASTAPI_HOST}/api/conversations/${targetSessionId}/mind-map`,
        {
          language: conversationLanguage || 'English',
          cognitive_level: String(conversationSupportLevel || 'intermediate').toLowerCase(),
          max_depth: 2,
          max_branches: 5,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      ));
      setMindMapDocument((current) => reconcileMindMap(current, response.data || null));
    } catch (e) {
      setMindMapError(e?.response?.data?.detail || e?.message || 'Unable to create the mind map.');
    } finally {
      setMindMapLoading(false);
    }
  };

  const setMindMapVisible = async (visible) => {
    setMemoryMapDialogOpen(visible);
    if (!visible) setMobileMindMapExpanded(false);
    if (visible) await loadMindMap(sessionId);
  };

  const generateStory = async () => {
    if (!sessionId) {
      setError('Missing session. Please upload or re-analyze the photo.');
      return;
    }
    if (conversationAnswers.length === 0) {
      setError('Please answer at least one question before generating the story.');
      return;
    }

    setError('');
    setGenerating(true);
    try {
      const payload = {
        session_id: sessionId,
        pages: Number(pageCount) || 6,
      };

      const res = await runWithAuthRetry((token) =>
        axios.post(`${FASTAPI_HOST}/api/conversations/${sessionId}/artifacts/story`, {
          pages: payload.pages,
          cognitive_level: String(cognitiveLevel || 'Intermediate').toLowerCase(),
          story_style: storyStyle,
          caregiver_notes: caregiverNotes.trim(),
          writing_language: storyLanguage || 'English',
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
      );

      setStoryTitle(res.data?.title || 'Scene Story');
      setStoryPages(Array.isArray(res.data?.pages) ? res.data.pages : []);
      setCaregiverTip(res.data?.caregiver_tip || '');
      setMediaUrl('');
      setMedia([]);
      setMediaError('');
      setMediaErrorSeverity('error');
      setTimeout(() => setStoryDialogOpen(true), 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to generate story.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateMedia = async (voice, imageStyle, lang, editedStoryPages, editedTitle) => {
    setMediaLoading(true);
    setMediaUrl('');
    setMedia([]);
    setError('');
    setMediaError('');
    setMediaErrorSeverity('error');

    try {
      const inputData = {
        name: childName || 'Child',
        age: Number(childAge) || 8,
        gender,
        environment_name: 'Real-life photo scene',
        cognitive_level: cognitiveLevel || 'Intermediate',
        visual_style: imageStyle || defaultImageStyle,
        skill_category: 'Photo_Story',
        skill_category_display: 'Photo Story',
        social_skill: 'Understanding real-life situations',
        interest: interest || 'Not specified',
        pages: (editedStoryPages || storyPagesForEditor).length || Number(pageCount) || 6,
        manual_environment_input: true,
        free_style_social_skill: true,
        language: lang || storyLanguage || 'English',
        voice: voice || ttsVoice,
        image_style: imageStyle || defaultImageStyle,
        content_type: 'photo_story',
        story_type: 3,
        story_title: editedTitle || storyTitle,
        raw_title: editedTitle || storyTitle,
      };

      const formData = new FormData();
      const conversationContext = {
        schema_version: 1,
        source: 'photo_story',
        session_id: sessionId,
        scene_summary: sceneSummary,
        messages: dialogMessages
          .filter((message) => ['assistant', 'user'].includes(message?.role) && message?.text)
          .map((message) => ({
            role: message.role,
            content: String(message.text),
            kind: message.kind || 'message',
          })),
        role_settings: {
          role_id: activeRoleId,
          child_name: childName || 'Child',
          child_age: childAge,
          gender,
          cognitive_level: cognitiveLevel,
          story_lang: storyLanguage || 'English',
          interest: interest || '',
          visual_style: visualStyle || '',
          image_style: imageStyle || '',
          narration_voice: ttsVoice || '',
        },
        ui_language: currentLang || 'English',
        conversation_language: currentLang || 'English',
        photo_story_settings: {
          conversation_support_level: conversationSupportLevel,
          story_style: storyStyle,
          page_count: Number(pageCount) || 6,
          caregiver_notes: caregiverNotes.trim(),
          leafy_voice: leafyVoice,
          leafy_speech_speed: Number(leafySpeechSpeed) || 1.0,
        },
      };
      formData.append('input_data', JSON.stringify(inputData));
      formData.append('story_pages', JSON.stringify(editedStoryPages || storyPagesForEditor));
      formData.append('conversation_context', JSON.stringify(conversationContext));
      if (editedTitle || storyTitle) formData.append('story_title', editedTitle || storyTitle);
      if (photoFile) formData.append('image', photoFile);

      const res = await runWithAuthRetry((token) =>
        axios.post(`${FASTAPI_HOST}/api/generate_media`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );

      setMediaUrl(res.data?.media_url || res.data?.image_url || res.data?.html_url || res.data?.static_url || '');
      setMedia(res.data?.media || []);
    } catch (e) {
      const status = e?.response?.status;
      const rawDetail = e?.response?.data?.detail;
      const detail = typeof rawDetail === 'string'
        ? rawDetail
        : rawDetail?.message || e?.message || (t?.media_error_message || 'Failed to generate media. Please try again.');
      if (status === 429 || String(detail).toLowerCase().includes('limit')) {
        setMediaError(
          t?.sb_media_limit
          || 'Story media generation limit has been reached. Please purchase or upgrade your plan.'
        );
        setMediaErrorSeverity('warning');
      } else {
        setMediaError(detail);
        setMediaErrorSeverity('error');
      }
    } finally {
      setMediaLoading(false);
    }
  };

  return (
    <BuddyWorkspace activity="photo_story" maxWidth={920} sx={{ pt: { xs: 9, sm: 9 }, pb: { xs: 2, md: 4 }, px: 2 }}>
      <Card sx={{ ...cardSx, overflow: 'hidden' }}>
        <WorkspaceHeader
          avatar={buddy.image}
          buddyId={buddy.id}
          avatarFallback={buddy.reactions?.neutral || '🙂'}
          title={(t?.photo_story_talk_with_leafy || 'Talk with Leafy to write story').replaceAll('Leafy', buddy.name)}
          actions={<>
            <ConversationStorageControls
              sessionId={sessionId}
              storageMode={storageMode}
              definitions={['photo_memory_interview']}
              onRestore={restoreConversationView}
              onStorageModeChange={setStorageMode}
              currentLang={currentLang}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 3 }}>
            <Tooltip title={analyzing
              ? (t?.photo_story_analyzing || 'Analyzing photo…')
              : photoFile
                ? (t?.photo_story_change_photo || 'Change Photo')
                : (t?.photo_story_upload_photo || 'Upload Photo')}
            >
              <span>
                <IconButton
                  onClick={openUploadDisclaimer}
                  disabled={!photoStoryStarted || analyzing || submittingAnswer || generating}
                  aria-label={photoFile
                    ? (t?.photo_story_change_photo || 'Change Photo')
                    : (t?.photo_story_upload_photo || 'Upload Photo')}
                  sx={{ border: '1px solid #d1d5db', color: '#6b7280' }}
                >
                  {analyzing
                    ? <CircularProgress size={20} color="inherit" />
                    : <CloudUploadRoundedIcon />}
                </IconButton>
              </span>
            </Tooltip>
            </Stack>
          </>}
        />
        {!photoStoryStarted ? (
          <Box sx={{ minHeight: 330, display: 'grid', placeItems: 'center', px: 2, py: 5, textAlign: 'center', bgcolor: '#FDFCF8' }}>
            <Stack spacing={2} alignItems="center" sx={{ maxWidth: 620 }}>
              <Typography role="note" sx={{ color: 'text.secondary', maxWidth: 520 }}>
                {t?.photo_story_desc || PHOTO_STORY_OPENING_MESSAGE}
              </Typography>
              <Button
                variant="contained"
                onClick={() => (hasPaidPlanAccess(userPlan) ? setPhotoStoryStarted(true) : onPurchase?.())}
                aria-label={t?.photo_story_start_aria || 'Start photo story conversation'}
                disabled={!userPlanChecked}
                sx={{ bgcolor: '#274D3B', borderRadius: 999, px: 4, py: 1.25, fontWeight: 800, '&:hover': { bgcolor: '#1F3D30' } }}
              >
                {t?.workspace_lets_start || 'Let’s start!'}
              </Button>
              {!userPlanChecked ? (
                <Typography variant="caption" color="text.secondary">{t?.workspace_checking_plan || 'Checking your plan…'}</Typography>
              ) : !hasPaidPlanAccess(userPlan) ? (
                <Typography variant="caption" color="text.secondary">{t?.workspace_paid_required || 'A paid plan is required to start this activity.'}</Typography>
              ) : null}
            </Stack>
          </Box>
        ) : (
        <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#FDFCF8' }}>
          <input
            ref={uploadInputRef}
            type="file"
            accept={IMAGE_UPLOAD_ACCEPT}
            onChange={handleSelectPhoto}
            style={{ display: 'none' }}
          />

          {photoPreviewUrl ? (
            <Box sx={{ px: 2, pt: 1.5, bgcolor: '#FDFCF8' }}>
              <Stack
                direction={photoPreviewExpanded ? 'column' : 'row'}
                spacing={1.25}
                alignItems={photoPreviewExpanded ? 'flex-start' : 'center'}
              >
                <Tooltip title={photoPreviewExpanded
                  ? (t?.photo_story_collapse_photo || 'Collapse photo')
                  : (t?.photo_story_expand_photo || 'Enlarge photo')}
                >
                  <ButtonBase
                    onClick={() => setPhotoPreviewExpanded((value) => !value)}
                    aria-label={photoPreviewExpanded
                      ? (t?.photo_story_collapse_photo || 'Collapse photo')
                      : (t?.photo_story_expand_photo || 'Enlarge photo')}
                    aria-expanded={photoPreviewExpanded}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #D6DED2',
                      width: photoPreviewExpanded ? '100%' : 58,
                      maxWidth: photoPreviewExpanded ? 560 : 58,
                    }}
                  >
                    <Box
                      component="img"
                      src={photoPreviewUrl}
                      alt={t?.photo_story_reference_photo || 'Reference photo'}
                      sx={{
                        display: 'block',
                        width: photoPreviewExpanded ? '100%' : 58,
                        height: photoPreviewExpanded ? 'auto' : 58,
                        maxHeight: photoPreviewExpanded ? '60vh' : 58,
                        objectFit: photoPreviewExpanded ? 'contain' : 'cover',
                        bgcolor: '#fff',
                      }}
                    />
                  </ButtonBase>
                </Tooltip>
                <Typography sx={{ fontWeight: 700, color: '#3A6752' }}>
                  {t?.photo_story_reference_photo || 'Reference photo'}
                </Typography>
              </Stack>
            </Box>
          ) : null}

          <Box sx={{ overflow: 'visible' }}>
              <Stack spacing={1.6}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      md: memoryMapDialogOpen && hasMemoryMapData
                        ? 'minmax(0, 1fr) minmax(280px, 340px)'
                        : 'minmax(0, 1fr)',
                    },
                    gap: 2,
                    alignItems: 'start',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack spacing={1.2}>
                      <ConversationTimeline
                        messages={chatMessages.map((message, index) => ({
                          id: `${message.role}-${index}-${String(message.text || '').slice(0, 12)}`,
                          role: message.role,
                          content: message.text,
                          reaction: message.reaction,
                        }))}
                        busy={submittingAnswer}
                        containerRef={chatContainerRef}
                        minHeight={330}
                        maxHeight="52vh"
                        emptyState={(
                          <Box
                            role="note"
                            sx={{
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              border: '1px solid #D6DED2',
                              bgcolor: '#E3E8DC',
                            }}
                          >
                            <Typography sx={{ color: '#1F352A', fontWeight: 600 }}>
                              {t?.photo_story_desc || PHOTO_STORY_OPENING_MESSAGE}
                            </Typography>
                          </Box>
                        )}
                        reactionRenderer={(message) => <Typography component="span" role="img" aria-label={`${buddy.name} reaction: ${message.reaction || 'neutral'}`} sx={{ fontSize: 18, lineHeight: 1 }}>{buddy.reactions?.[message.reaction] || buddy.reactions?.neutral || '🙂'}</Typography>}
                      />

                      <WorkspaceComposerArea sx={{ position: 'relative', zIndex: 5 }}>
                      {hasMemoryMapData ? (
                        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
                          <Button
                            size="small"
                            onClick={async () => {
                              if (!memoryMapDialogOpen) await setMindMapVisible(true);
                              setMobileMindMapExpanded((open) => !open);
                            }}
                            aria-expanded={mobileMindMapExpanded}
                            sx={{
                              borderRadius: 999,
                              px: 1.5,
                              bgcolor: '#E4F4D8',
                              color: '#36543F',
                              fontWeight: 900,
                              textTransform: 'none',
                            }}
                          >
                            🌱 {compactMindMapLabel} · {mindMapDocument?.root?.children?.length || conversationAnswers.length}
                            {mindMapLoading ? <CircularProgress size={12} sx={{ ml: 0.8 }} /> : null}
                          </Button>
                          {mobileMindMapExpanded ? (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 'calc(100% + 8px)',
                                maxHeight: '50vh',
                                overflowY: 'auto',
                                bgcolor: '#FFFDF3',
                                border: '2px solid #B8D5B5',
                                borderRadius: 3,
                                boxShadow: 8,
                                zIndex: 20,
                              }}
                            >
                              <MindMapPanel
                                document={mindMapDocument}
                                loading={mindMapLoading}
                                error={mindMapError}
                                photoUrl={photoPreviewUrl}
                                language={conversationLanguage}
                              />
                            </Box>
                          ) : null}
                        </Box>
                      ) : null}
                      {!conversationDone ? (
                        <Stack spacing={1}>
                          <ConversationScaffoldAids
                            choices={currentChoices}
                            hint={currentHint}
                            visible={showScaffoldAids}
                            disabled={submittingAnswer || analyzing || !currentQuestion}
                            language={conversationLanguage}
                            onSelect={commitCurrentAnswerAndNext}
                            onSpeak={(value) => speakText(value, { allowBrowserFallback: false })}
                            onSkip={(label) => sendConversationAction('skip_question', label)}
                            trailingActions={<>
                              <FormControlLabel
                                control={(
                                  <Switch
                                    checked={showScaffoldAids}
                                    onChange={(event) => setShowScaffoldAids(event.target.checked)}
                                    size="small"
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#3A6752' },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3A6752' },
                                    }}
                                  />
                                )}
                                label={showScaffoldAids
                                  ? (t?.photo_story_options_on || 'Support ON')
                                  : (t?.photo_story_options_off || 'Support OFF')}
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 700 } }}
                              />
                              <FormControlLabel
                                control={(
                                  <Switch
                                    checked={memoryMapDialogOpen}
                                    onChange={(event) => setMindMapVisible(event.target.checked)}
                                    disabled={!hasMemoryMapData}
                                    size="small"
                                    sx={{
                                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#3A6752' },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3A6752' },
                                    }}
                                  />
                                )}
                                label={memoryMapDialogOpen
                                  ? (t?.photo_story_memory_map_on || 'Mind map ON')
                                  : (t?.photo_story_memory_map_off || 'Mind map OFF')}
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 700 } }}
                              />
                              <Tooltip
                                title={voiceBroadcastEnabled
                                  ? (t?.photo_story_disable_voice_broadcast || 'Turn off Buddy voice')
                                  : (t?.photo_story_enable_voice_broadcast || 'Turn on Buddy voice')}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={toggleVoiceBroadcast}
                                    disabled={submittingAnswer || analyzing || !latestPlayableAssistantText}
                                    aria-label={voiceBroadcastEnabled
                                      ? (t?.photo_story_disable_voice_broadcast || 'Turn off Buddy voice')
                                      : (t?.photo_story_enable_voice_broadcast || 'Turn on Buddy voice')}
                                    aria-pressed={voiceBroadcastEnabled}
                                    sx={{
                                      border: '1px solid #d1d5db',
                                      color: voiceBroadcastEnabled ? '#2f7d32' : '#6b7280',
                                    }}
                                  >
                                    {voiceBroadcastEnabled ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>}
                          />

                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                            <TextField
                              label=""
                              autoComplete="off"
                              value={currentAnswer}
                              onFocus={() => setMobileMindMapExpanded(false)}
                              onChange={(e) => setCurrentAnswer(e.target.value)}
                              fullWidth
                              disabled={submittingAnswer || analyzing}
                              placeholder={t?.photo_story_type_message_placeholder || 'Type your message...'}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: '#ffffff',
                                },
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {/* Follow conversationLanguage so its source can be separated from UI language later. */}
                                    <MicTranscribeButton
                                      insideInput
                                      currentLang={conversationLanguage}
                                      followUiLanguage
                                      disabled={submittingAnswer || analyzing}
                                      onRecordingStart={stopSpeaking}
                                      onTranscript={(text) => {
                                        const spoken = (text || '').trim();
                                        if (!spoken) return;
                                        setCurrentAnswer(spoken);
                                      }}
                                      onError={(msg) => setError(msg)}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                            />
                            <Button
                              variant="contained"
                              endIcon={<SendRoundedIcon />}
                              onClick={() => commitCurrentAnswerAndNext(currentAnswer)}
                              aria-label="send answer"
                              disabled={submittingAnswer || analyzing || !String(currentAnswer || '').trim()}
                              sx={{
                                bgcolor: '#3A6752',
                                minWidth: 112,
                                '&:hover': { bgcolor: '#274D3B' },
                              }}
                            >
                              {t?.photo_story_send || 'Send'}
                            </Button>
                          </Stack>
                          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <EndConversationControl
                              label={t?.photo_story_end || 'End conversation'}
                              disabled={submittingAnswer}
                              onEnd={(label) => sendConversationAction('end_conversation', label)}
                            />
                          </Stack>
                        </Stack>
                      ) : canAddConversationDetail ? (
                        <Stack spacing={1}>
                          <Typography sx={{ fontWeight: 700, color: '#111111', mt: 0.5 }}>
                            {currentQuestion}
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
                                <TextField
                                  label=""
                                  autoComplete="off"
                                  value={currentAnswer}
                                  onFocus={() => setMobileMindMapExpanded(false)}
                                  onChange={(e) => setCurrentAnswer(e.target.value)}
                                  fullWidth
                                  placeholder="Add an optional detail"
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <MicTranscribeButton
                                          insideInput
                                          currentLang={conversationLanguage}
                                          followUiLanguage
                                          disabled={submittingAnswer || analyzing}
                                          onRecordingStart={stopSpeaking}
                                          onTranscript={(text) => {
                                            const spoken = (text || '').trim();
                                            if (!spoken) return;
                                            setCurrentAnswer(spoken);
                                          }}
                                          onError={(msg) => setError(msg)}
                                        />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                <Button
                                  variant="contained"
                                  endIcon={<SendRoundedIcon />}
                                  onClick={() => commitCurrentAnswerAndNext(currentAnswer)}
                                  aria-label="submit final input"
                                  disabled={!String(currentAnswer || '').trim() || submittingAnswer || analyzing}
                                  sx={{
                                    bgcolor: '#3A6752',
                                    minWidth: 112,
                                    '&:hover': { bgcolor: '#274D3B' },
                                  }}
                                >
                                  {t?.photo_story_send || 'Send'}
                                </Button>
                          </Stack>
                        </Stack>
                      ) : null}
                      </WorkspaceComposerArea>
                    </Stack>
                  </Box>
                  {memoryMapDialogOpen && hasMemoryMapData ? (
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'sticky',
                        top: 12,
                        maxHeight: '68vh',
                        overflowY: 'auto',
                        borderRadius: 4,
                      }}
                    >
                      <MindMapPanel
                        document={mindMapDocument}
                        loading={mindMapLoading}
                        error={mindMapError}
                        photoUrl={photoPreviewUrl}
                        language={conversationLanguage}
                      />
                    </Box>
                  ) : null}
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="flex-end"
                >
                  {storyPages.length > 0 ? (
                    <Button
                      variant="outlined"
                      onClick={() => setStoryDialogOpen(true)}
                      sx={{
                        minHeight: 40,
                        fontWeight: 700,
                        color: '#111111',
                        borderColor: '#111111',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          borderColor: '#111111',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {t?.photo_story_preview_text_story || 'Preview Text Story'}
                    </Button>
                  ) : null}
                  <TextField
                    select
                    size="small"
                    label={t?.photo_story_settings_title || 'Creative Story Style'}
                    value={storyStyle}
                    onChange={handleStoryStyleChange}
                    sx={{ minWidth: { xs: '100%', sm: 260 } }}
                  >
                    {PHOTO_STORY_STYLES.map((styleOption) => (
                      <MenuItem key={styleOption.value} value={styleOption.value}>
                        {t?.[styleOption.labelKey] || styleOption.label} — {t?.[styleOption.descriptionKey] || styleOption.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    onClick={generateStory}
                    disabled={!canGenerate}
                    variant="contained"
                    startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoStoriesRoundedIcon />}
                    sx={{
                      bgcolor: '#C96F2D',
                      minWidth: 130,
                      minHeight: 40,
                      '&:hover': { bgcolor: '#A9561F' },
                      fontWeight: 800,
                      color: '#ffffff',
                    }}
                  >
                    {generating
                      ? (t?.photo_story_writing_story || 'Writing Story...')
                      : (t?.photo_story_write_story || 'Write Story')}
                  </Button>
                </Stack>
              </Stack>
          </Box>

          {error ? (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                backgroundColor: '#f3f4f6',
                color: '#111111',
                '& .MuiAlert-icon': { color: '#111111' },
              }}
            >
              {error}
            </Alert>
          ) : null}
        </Stack>
        )}
      </Card>

      <BuddyManagerDialog open={buddySettingsOpen} onClose={() => setBuddySettingsOpen(false)} buddyId={buddy.id} voice={leafyVoice} speed={leafySpeechSpeed} currentLang={currentLang} t={t} onBuddySelected={setBuddy} onVoiceSettingsChanged={({ voice, speed }) => { setLeafyVoice(voice); setLeafySpeechSpeed(speed); }} />

      <Dialog
        open={uploadDisclaimerOpen}
        onClose={closeUploadDisclaimer}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 2400 }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {t?.photo_story_upload_disclaimer_title || 'Before you upload'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#111111', whiteSpace: 'pre-wrap' }}>
            {t?.photo_story_upload_disclaimer_text
              || "Make sure you have the necessary rights to any images or files you upload. Don't generate content that infringes on others' rights, including content that deceives, harasses, or harms."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeUploadDisclaimer} sx={{ fontWeight: 800, color: '#111111' }}>
            {t?.photo_story_upload_disclaimer_cancel || t?.sb_cancel || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadAgree}
            sx={{ bgcolor: '#274D3B', '&:hover': { bgcolor: '#1F3D30' }, fontWeight: 800 }}
          >
            {t?.photo_story_upload_disclaimer_agree || 'Agree'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={roleSettingDialogOpen}
        onClose={() => setRoleSettingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 2300 }}
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '88vh', bgcolor: '#FBF8EF' } }}
      >
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <CreationSettingsPage
            name={childName}
            setName={setChildName}
            gender={gender}
            setGender={setGender}
            visualStyle={visualStyle}
            setVisualStyle={setVisualStyle}
            age={childAge}
            setAge={setChildAge}
            interest={interest}
            setInterest={setInterest}
            voice={ttsVoice}
            setVoice={setTtsVoice}
            cognitiveLevel={cognitiveLevel}
            setCognitiveLevel={setCognitiveLevel}
            conversationLevel={conversationLevel}
            setConversationLevel={setConversationLevel}
            pages={rolePages}
            setPages={setRolePages}
            imageStyle={imageStyle}
            setImageStyle={setImageStyle}
            getImageStylesInLanguage={getImageStylesInLanguage}
            cognitiveLevelsData={cognitiveLevelsData}
            currentLang={currentLang}
            t={t}
            visualStylesData={visualStylesData}
            voicesData={voicesData}
            onUiLanguageChange={onUiLanguageChange}
            storyLanguage={storyLanguage}
            setStoryLanguage={setStoryLanguage}
            onClose={() => setRoleSettingDialogOpen(false)}
            onOpenBuddySettings={() => {
              setRoleSettingDialogOpen(false);
              setBuddySettingsOpen(true);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={storyDialogOpen}
        onClose={() => setStoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 2200 }}
      >
        <DialogContent sx={{ position: 'relative', p: { xs: 0, sm: 3 }, mt: { xs: '56px', sm: 0 } }}>
          <TextStoryPage
            storyTitle={storyTitle}
            storyPages={storyPagesForEditor}
            storyLanguage={storyLanguage}
            voice={ttsVoice}
            imageStyle={defaultImageStyle}
            onGenerateMedia={handleGenerateMedia}
            mediaUrl={mediaUrl}
            media={media}
            mediaLoading={mediaLoading}
            mediaError={mediaError}
            mediaErrorSeverity={mediaErrorSeverity}
            t={t}
            userPlan={userPlan}
            onClose={() => setStoryDialogOpen(false)}
            showMediaLoadingDialog={false}
          />
        </DialogContent>
      </Dialog>

      {/* Keep media-generation progress visible even if the text-story dialog is closed. */}
      <Dialog open={mediaLoading} maxWidth="xs" fullWidth sx={{ zIndex: 2500 }}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <LinearProgress sx={{ width: '100%', mb: 2 }} />
          <Typography variant="h6" align="center">
            {t?.media_generation_lbl || 'Generating Media...'}
          </Typography>
        </DialogContent>
      </Dialog>
    </BuddyWorkspace>
  );
}
